import React from 'react';

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

type SliderMarks = Record<number, React.ReactNode> | Array<{ value: number; label: React.ReactNode }>;
type SliderValueFormatter = (value: number) => string;
type NativeRangeInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'children' | 'className' | 'disabled' | 'max' | 'min' | 'onChange' | 'size' | 'step' | 'type' | 'value'
>;

interface SliderBaseProps {
  min: number;
  max: number;
  step?: number;
  marks?: SliderMarks;
  formatValue?: SliderValueFormatter;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  disabled?: boolean;
  tone?: 'default' | 'inverse';
  'data-testid'?: string;
}

export interface FmSliderProps extends SliderBaseProps, NativeRangeInputProps {
  value?: number | null;
  onChange?: (value: number) => void;
  label?: React.ReactNode;
  nullValueLabel?: React.ReactNode;
}

export interface FmRangeSliderProps extends SliderBaseProps {
  value: [number, number];
  onChange?: (value: [number, number]) => void;
  label: React.ReactNode;
  lowerLabel?: string;
  upperLabel?: string;
}

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  return Math.min(Math.max(value, min), max);
}

function parseSliderValue(value: string): number {
  return Number.parseFloat(value);
}

function formatDefault(value: number): string {
  return String(value);
}

function normalizeMarks(marks: SliderMarks | undefined): Array<{ value: number; label: React.ReactNode }> {
  if (!marks) return [];
  if (Array.isArray(marks)) return marks;
  return Object.entries(marks)
    .map(([value, label]) => ({ value: Number(value), label }))
    .filter((mark) => !Number.isNaN(mark.value))
    .sort((a, b) => a.value - b.value);
}

function formatRangeValue(
  value: [number, number],
  formatValue: SliderValueFormatter | undefined,
): string {
  const formatter = formatValue ?? formatDefault;
  return `${formatter(value[0])} - ${formatter(value[1])}`;
}

function SliderMarksList({ marks, min, max }: { marks?: SliderMarks; min: number; max: number }) {
  const normalized = normalizeMarks(marks);
  if (normalized.length === 0) return null;

  return (
    <div className="fm-slider__marks" aria-hidden="true">
      {normalized.map((mark) => {
        const position = ((clamp(mark.value, min, max) - min) / (max - min)) * 100;
        return (
          <span
            key={mark.value}
            className="fm-slider__mark"
            style={{ insetInlineStart: `${position}%` }}
          >
            {mark.label}
          </span>
        );
      })}
    </div>
  );
}

export const FmSlider: React.FC<FmSliderProps> = ({
  min,
  max,
  step = 1,
  value,
  onChange,
  label,
  nullValueLabel = 'Baseline',
  marks,
  formatValue = formatDefault,
  className,
  disabled,
  tone = 'default',
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  ...inputProps
}) => {
  const resolvedValue = clamp(value ?? min, min, max);
  const hasValue = value != null;
  const labelId = React.useId();
  const accessibleName = typeof label === 'string' ? label : ariaLabel;

  return (
    <div
      className={cx('fm-slider', tone === 'inverse' && 'fm-slider--inverse', className)}
      data-testid={dataTestId}
    >
      {label ? (
        <div className="fm-slider__header">
          <span id={labelId} className="fm-slider__label">
            {label}
          </span>
          <span className="fm-slider__value">
            {hasValue ? formatValue(resolvedValue) : nullValueLabel}
          </span>
        </div>
      ) : null}
      <input
        {...inputProps}
        className="fm-slider__input"
        type="range"
        min={min}
        max={max}
        step={step}
        value={resolvedValue}
        disabled={disabled}
        aria-label={!label ? accessibleName : undefined}
        aria-labelledby={label ? labelId : undefined}
        aria-valuetext={hasValue ? formatValue(resolvedValue) : String(nullValueLabel)}
        onChange={(event) => {
          onChange?.(clamp(parseSliderValue(event.currentTarget.value), min, max));
        }}
      />
      <SliderMarksList marks={marks} min={min} max={max} />
    </div>
  );
};

export const FmRangeSlider: React.FC<FmRangeSliderProps> = ({
  min,
  max,
  step = 1,
  value,
  onChange,
  label,
  lowerLabel = 'Minimum',
  upperLabel = 'Maximum',
  marks,
  formatValue = formatDefault,
  className,
  disabled,
  tone = 'default',
  'data-testid': dataTestId,
}) => {
  const lower = clamp(Math.min(value[0], value[1]), min, max);
  const upper = clamp(Math.max(value[0], value[1]), min, max);
  const labelId = React.useId();
  const lowerId = React.useId();
  const upperId = React.useId();
  const formattedRange = formatRangeValue([lower, upper], formatValue);

  return (
    <div
      className={cx('fm-slider fm-slider--range', tone === 'inverse' && 'fm-slider--inverse', className)}
      data-testid={dataTestId}
    >
      <div className="fm-slider__header">
        <span id={labelId} className="fm-slider__label">
          {label}
        </span>
        <span className="fm-slider__value">{formattedRange}</span>
      </div>
      <div className="fm-range-slider__controls">
        <input
          className="fm-slider__input fm-range-slider__input fm-range-slider__input--lower"
          type="range"
          min={min}
          max={max}
          step={step}
          value={lower}
          disabled={disabled}
          aria-labelledby={`${labelId} ${lowerId}`}
          aria-valuetext={formatValue(lower)}
          onChange={(event) => {
            const nextLower = clamp(parseSliderValue(event.currentTarget.value), min, upper);
            onChange?.([nextLower, upper]);
          }}
        />
        <input
          className="fm-slider__input fm-range-slider__input fm-range-slider__input--upper"
          type="range"
          min={min}
          max={max}
          step={step}
          value={upper}
          disabled={disabled}
          aria-labelledby={`${labelId} ${upperId}`}
          aria-valuetext={formatValue(upper)}
          onChange={(event) => {
            const nextUpper = clamp(parseSliderValue(event.currentTarget.value), lower, max);
            onChange?.([lower, nextUpper]);
          }}
        />
      </div>
      <span id={lowerId} className="sr-only">
        {lowerLabel}
      </span>
      <span id={upperId} className="sr-only">
        {upperLabel}
      </span>
      <SliderMarksList marks={marks} min={min} max={max} />
    </div>
  );
};

export default FmSlider;
